import { createStyles } from '@/contexts/theme-context';
import {
  Typography,
  TypographyProps,
  TypographyVariant,
} from '@usepepper/sprout-kit-ui';
import { View } from 'react-native';

const getMiddleTruncationSplitIndex = (text: string): number => {
  // Short strings split near 1/2; longer strings split closer to 2/3 to
  // compensate for the flex layout where the second half never shrinks.
  const ratio = Math.min(2 / 3, 0.5 + text.length / 200);
  let mid = Math.ceil(text.length * ratio);

  // Shift past any spaces at the split boundary so they end up inside the
  // first half since RN Text silently drops leading/trailing whitespace.
  while (
    mid < text.length - 1 &&
    (text[mid] === ' ' || text[mid - 1] === ' ')
  ) {
    mid++;
  }
  return mid;
};

interface MiddleTruncatedTypographyProps
  extends Omit<TypographyProps, 'children' | 'bold'> {
  bold?: true;
  text: string;
  variant: TypographyVariant;
}

/**
 * Renders text with middle truncation: the start and end of the string are
 * preserved while the middle is elided when space is constrained.
 *
 * Uses two flex containers — the first half (`flexShrink: 1`) absorbs the
 * squeeze with standard tail truncation while the second half (`flexShrink: 0`)
 * keeps its full width. The split point is shifted past word boundaries so
 * that spaces never fall at the edge of a `Text` element (RN silently drops
 * leading/trailing whitespace there).
 *
 * @param text - The full string to render with middle truncation.
 * @param variant - Sprout Kit typography variant (e.g. `"body"`, `"heading"`).
 */
export const MiddleTruncatedTypography: React.FC<
  MiddleTruncatedTypographyProps
> = ({ text, bold, ...typographyProps }) => {
  const [styles] = useStyles();
  const midpoint = getMiddleTruncationSplitIndex(text);

  return (
    <View style={styles.container}>
      <View style={styles.start}>
        {/* Tamagui boolean variants only accept `true`, not `boolean | undefined`.
            Conditional spread passes `bold` only when set to avoid the type mismatch. */}
        <Typography
          {...typographyProps}
          {...(bold && { bold })}
          numberOfLines={1}
        >
          {text.slice(0, midpoint)}
        </Typography>
      </View>
      <View style={styles.end}>
        <Typography
          {...typographyProps}
          {...(bold && { bold })}
          numberOfLines={1}
        >
          {text.slice(midpoint)}
        </Typography>
      </View>
    </View>
  );
};

const useStyles = createStyles(() => ({
  container: {
    flexDirection: 'row',
    flexShrink: 1,
    overflow: 'hidden',
  },
  end: {
    flexShrink: 0,
  },
  start: {
    flexShrink: 1,
    overflow: 'hidden',
  },
}));
